<h4>{{ editing ? 'Edit' : 'Add' }} smart table</h4>
<div class="flex items-start gap-4">
	<div class="space-y-3 w-full">
		<div class="flex items-center gap-3 text-sm">
			<div class="w-4/6">
				<app-dropdown
					(onSelect)="selectContext($event)"
					[ngModel]="FriendlyContextAccessors[selectedContext]"
					name="selectedContext"
					label="Select the group of items you would like to loop through"
					[itemGroups]="[{ items: contextOptions }]"
				></app-dropdown>
			</div>
			<app-number-toggle
				label="Select number of columns"
				[ngModel]="numOfColumns"
				(ngModelChange)="updateNumberOfColumns($event)"
				[min]="1"
				[max]="6"
			></app-number-toggle>
			<app-icon
				[icon]="ICON.INFO"
				class="mt-5"
				matTooltip="This table iterates through tags added to the body of the table, which are filtered based on the group selected in the dropdown"
			></app-icon>
		</div>
		<div class="mt-5">
			<p class="font-bold text-lg">Editor</p>
			<div id="mytoolbar" class="h-10"></div>
			<div class="px-4">
				<div class="p-2">
					<div class="flex items-center gap-2 my-3">
						<p class="text-lg w-20">Header</p>
						<app-checkbox
							[ngModel]="showHeader"
							label="Show header"
							(ngModelChange)="toggleShowHeader()"
							type="clear"
						></app-checkbox>
					</div>
					<table
						class="my-2 w-full"
						*ngIf="numOfColumns > 0 && selectedContext"
						[@openClose]
					>
						<tbody *ngIf="showHeader">
							<tr class="p-1">
								<ng-container
									*ngFor="let cell of headerRow.cells; let i = index"
								>
									<td
										class="cell"
										width="{{ columnWidths[i] }}%"
										(click)="selectCell('header', 0, i)"
										[attr.colspan]="cell.colspan"
									>
										<editor
											class="h-full w-full min-h-[40px] border border-gray-300 p-2"
											[init]="tmceInit"
											[ngModel]="cell.text"
											(ngModelChange)="updateCellText($event, 'header', i)"
											id="{{ 'header-' + i }}"
										></editor>
									</td>
								</ng-container>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="p-2 rounded-lg bg-grey-lightest">
					<div class="flex items-center gap-2 my-3">
						<p class="text-lg w-20">Body</p>
						<app-number-toggle
							(ngModelChange)="updateBodyRows($event)"
							[ngModel]="bodyRows.length"
							[min]="1"
						></app-number-toggle>
					</div>
					<p class="text-sm">
						Values in the body rows will loop through items within the selected
						group
					</p>
					<table
						class="my-2 w-full"
						*ngIf="numOfColumns > 0 && selectedContext"
						[@openClose]
					>
						<tbody>
							<tr *ngFor="let row of bodyRows; let i = index">
								<ng-container *ngFor="let cell of row.cells; let j = index">
									<td
										class="cell"
										width="{{ columnWidths[j] }}%"
										[ngClass]="{ 'bg-blue-100': cell === selectedCell }"
										(click)="selectCell('body', i, j)"
										[attr.colspan]="cell.colspan"
									>
										<editor
											class="h-full w-full min-h-[40px] border border-gray-300 p-2 bg-white"
											[init]="tmceInit"
											[ngModel]="cell.text"
											(ngModelChange)="updateCellText($event, 'body', j, i)"
											id="{{ 'body-' + j + '-' + i }}"
										></editor>
									</td>
								</ng-container>
							</tr>
						</tbody>
					</table>
				</div>
				<div class="p-2">
					<div class="flex items-center gap-2 my-3">
						<p class="text-lg w-20">Footer</p>
						<app-number-toggle
							(ngModelChange)="updateFooterRows($event)"
							[ngModel]="footerRows.length"
							[min]="0"
						></app-number-toggle>
					</div>
					<table
						class="my-2 w-full"
						*ngIf="numOfColumns > 0 && selectedContext"
						[@openClose]
					>
						<tfoot>
							<tr *ngFor="let row of footerRows; let i = index">
								<ng-container *ngFor="let cell of row.cells; let j = index">
									<td
										class="cell"
										width="{{ columnWidths[j] }}%"
										[ngClass]="{ 'bg-yellow-100': cell === selectedCell }"
										(click)="selectCell('footer', i, j)"
										[attr.colspan]="cell.colspan"
									>
										<editor
											class="h-full w-full min-h-[40px] border border-gray-300 p-2"
											[init]="tmceInit"
											[ngModel]="cell.text"
											(ngModelChange)="updateCellText($event, 'footer', j, i)"
											id="{{ 'footer-' + j + '-' + i }}"
										></editor>
									</td>
								</ng-container>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>
		</div>
		<div class="rounded-lg bg-white">
			<p class="font-bold text-lg">Preview</p>
			<div
				class="text-left p-4 preview"
				[innerHTML]="previewHTML | sanitize"
			></div>
		</div>
	</div>
	<div class="w-96 flex flex-col gap-4">
		<app-radio-group
			[items]="leftColumnOptions"
			type="compact"
			[(ngModel)]="leftColumnView"
		></app-radio-group>
		<div class="flex flex-col gap-4" *ngIf="leftColumnView === 'settings'">
			<app-container type="light" title="Table formatting" [collapsable]="true">
				<div class="flex flex-col">
					<p class="font-semibold pb-2">Column widths</p>
					<app-checkbox
						class="text-sm"
						[(ngModel)]="equalWidthColumns"
						(ngModelChange)="updateEqualColumnWidths()"
						type="clear"
						label="Apply same width"
					></app-checkbox>
					<div class="grid grid-cols-2 gap-2 pt-2" *ngIf="!equalWidthColumns">
						<app-input
							class="text-sm"
							*ngFor="
								let col of columnWidths;
								let i = index;
								trackBy: trackByIndex
							"
							[(ngModel)]="columnWidths[i]"
							(ngModelChange)="updatePreviewHTML()"
							type="number"
							label="Column {{ i + 1 }}"
						></app-input>
					</div>
				</div>
				<div class="flex flex-col gap-1 mt-2">
					<p class="font-semibold pb-1">Row heights</p>
					<app-checkbox
						class="text-sm"
						[(ngModel)]="customHeights"
						type="clear"
						label="Custom row heights"
					></app-checkbox>
					<div class="flex flex-col ml-2 gap-2 mt-2" *ngIf="customHeights">
						<app-checkbox
							class="text-sm"
							[(ngModel)]="customHeaderHeight"
							type="clear"
							label="Custom header height"
						></app-checkbox>
						<app-input
							class="text-sm"
							*ngIf="customHeaderHeight"
							[(ngModel)]="headerRowHeight"
							(ngModelChange)="updatePreviewHTML()"
							type="number"
						></app-input>
						<app-checkbox
							class="text-sm"
							[(ngModel)]="customBodyHeight"
							type="clear"
							label="Custom body height"
						></app-checkbox>
						<app-input
							class="text-sm"
							*ngIf="customBodyHeight"
							[(ngModel)]="bodyRowHeight"
							(ngModelChange)="updatePreviewHTML()"
							type="number"
						></app-input>
						<app-checkbox
							class="text-sm"
							[(ngModel)]="customFooterHeight"
							type="clear"
							label="Custom footer height"
						></app-checkbox>
						<app-input
							class="text-sm"
							*ngIf="customFooterHeight"
							[(ngModel)]="footerRowHeight"
							(ngModelChange)="updatePreviewHTML()"
							type="number"
						></app-input>
					</div>
				</div>
				<div class="flex flex-col gap-2 mt-4">
					<p class="font-semibold">Border options</p>
					<div class="flex items-center gap-2">
						<app-colour-picker
							class="w-fit"
							label="Border colour"
							[(ngModel)]="borderColor"
							(ngModelChange)="updatePreviewHTML()"
						></app-colour-picker>
						<app-dropdown
							(onSelect)="selectBorderWidth($event)"
							[ngModel]="borderWidth || '0px'"
							name="borderWidth"
							[itemGroups]="[{ items: borderWidthOptions }]"
							class="flex-none w-20"
						></app-dropdown>
					</div>
				</div>
				<div class="flex flex-col gap-2 mt-4">
					<p class="font-semibold">Background options</p>
					<div class="flex flex-col text-sm gap-2">
						<div class="flex items-center gap-2">
							<p>Colour</p>
							<app-colour-picker
								*ngIf="!zebraStripe"
								[(ngModel)]="bgColor"
								(ngModelChange)="updatePreviewHTML()"
							></app-colour-picker>
						</div>
						<app-checkbox
							[(ngModel)]="headerBG"
							label="Apply grey header background"
							(ngModelChange)="updatePreviewHTML()"
							type="clear"
						></app-checkbox>
						<app-checkbox
							[(ngModel)]="zebraStripe"
							label="Show zebra stripes"
							(ngModelChange)="updatePreviewHTML()"
							type="clear"
						></app-checkbox>
					</div>
				</div>
			</app-container>
			<app-container
				type="light"
				title="Merge horizontally"
				[collapsable]="true"
			>
				<div class="flex flex-col gap-4 items-center justify-center">
					<div class="bg-gray-50 p-2 flex gap-2 items-center justify-center">
						<app-icon [icon]="ICON.INFO"></app-icon>
						<p class="text-sm">
							Select a cell in the table and click the button below to merge
							with the cell on its right
						</p>
					</div>
					<app-button btn="secondary" (onClick)="mergeRight()"
						>Merge right</app-button
					>
				</div>
			</app-container>
		</div>
		<div class="flex flex-col gap-4" *ngIf="leftColumnView === 'tags'">
			<div class="w-full flex items-center gap-3 bg-gray-50 rounded-lg p-2">
				<app-icon [icon]="ICON.INFO"></app-icon>
				<p class="text-sm">
					To use tags, click
					<span><app-icon [icon]="ICON.COPY"></app-icon></span> to copy a tag,
					and paste within a body cell
				</p>
			</div>
			<app-input
				placeholder="Search tags"
				(ngModelChange)="searchValueChanged($event)"
				[ngModel]="searchValue"
			></app-input>
			<cdk-virtual-scroll-viewport
				itemSize="50"
				class="h-[44rem] overflow-y-auto overflow-x-hidden border border-grey-lighter p-2 w-full"
			>
				<p *ngIf="searchValues?.length === 0">No tags match that search.</p>
				<div class="w-full">
					<div
						*cdkVirtualFor="
							let tag of searchValues;
							let i = index;
							trackBy: trackByLabel
						"
						class="px-1 my-1 rounded"
						[id]="tag"
					>
						<div class="p-1 flex items-center justify-between">
							<p class="truncate pb-1"><span [innerHTML]="tag"></span></p>
							<app-icon
								class="cursor-pointer"
								(click)="copyToClipboard(tag)"
								[icon]="ICON.COPY"
							></app-icon>
						</div>
					</div>
				</div>
			</cdk-virtual-scroll-viewport>
		</div>
	</div>
</div>
<div class="flex items-center justify-between w-full mt-4">
	<app-button (onClick)="closeModal()" btn="tertiary">Cancel</app-button>
	<app-button
		(onClick)="addTable()"
		[disabled]="!selectedContext || !bodyRows.length"
		>{{ editing ? 'Save' : 'Create' }}</app-button
	>
</div>
